import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authenticatedFetch } from '@/lib/auth';
import {
  Shield, ClipboardCheck, CheckCircle2, Loader2, AlertCircle,
  ArrowRight, FileSignature
} from 'lucide-react';

/**
 * Sammenhengende onboarding-flyt: DPA (steg 1) + kvalitetskontrakt (steg 2).
 * Viser progress på tvers av begge steg og guider brukeren sekvensielt.
 *
 * Kalles fra OnboardingGuard når enten DPA eller kvalitetskontrakt mangler.
 */

interface StepStatus {
  key: 'dpa' | 'quality';
  title: string;
  subtitle: string;
  icon: typeof Shield;
  done: boolean;
  required: boolean;
  actionLabel: string;
  actionHref: string;
  blocked?: boolean;         // Kan ikke starte ennå (f.eks. mal ikke publisert)
  blockedReason?: string;
  detail?: string;
}

const DPA_LABELS: Record<string, string> = {
  kompetanseforbundet_studieforbund: 'Databehandleravtale for studieforbund',
  kompetanseforbundet_medlem: 'Databehandleravtale for medlemsorganisasjon',
  lms_addendum: 'Databehandleravtale — LMS-tillegg',
  fellesskapsforpliktelse_studieforbund: 'Fellesskapsforpliktelse for studieforbund'
};

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [steps, setSteps] = useState<StepStatus[]>([]);
  const [allDone, setAllDone] = useState(false);
  const [userName, setUserName] = useState<string>('');

  useEffect(() => {
    (async () => {
      try {
        const [qRes, dRes] = await Promise.all([
          authenticatedFetch('/api/onboarding/status').then(r => r.json()).catch(() => ({ success: false })),
          authenticatedFetch('/api/dpa/status').then(r => r.json()).catch(() => ({ dpa_status: [] }))
        ]);

        if (qRes?.user?.full_name) setUserName(qRes.user.full_name);

        const stepList: StepStatus[] = [];

        // DPA-status og fellesskapsforpliktelse — hver som eget steg.
        // Studieforbund-admin skal se: (1) Fellesskapsforpliktelse (2) DPA-1.
        // Medlem-admin: (1) DPA-2 (evt. DPA-3 hvis LMS aktivert).
        // Filtrer ut can_sign=false (f.eks. studieforbund-admin ser ikke DPA-2 for eget medlem)
        // og LMS-tillegg (signeres kun ved LMS-integrasjon).
        //
        // Rekkefølge: fellesskapsforpliktelse først (foretaksmessig deltagelse i ordningen),
        // deretter DPA (personvern). Innen samme kategori: studieforbund → medlem → LMS.
        const DPA_SORT_ORDER: Record<string, number> = {
          fellesskapsforpliktelse_studieforbund: 1,
          kompetanseforbundet_studieforbund: 2,
          kompetanseforbundet_medlem: 3,
          lms_addendum: 4,
        };
        const dpaList: any[] = Array.isArray(dRes.dpa_status) ? dRes.dpa_status : [];
        const relevantDpa = dpaList
          .filter(d => d.dpa_type !== 'lms_addendum' && d.can_sign !== false)
          .sort((a, b) => (DPA_SORT_ORDER[a.dpa_type] || 99) - (DPA_SORT_ORDER[b.dpa_type] || 99));

        for (const d of relevantDpa) {
          const label = DPA_LABELS[d.dpa_type] || d.dpa_type;
          const done = d.signed && d.is_current_version_signed;
          const blocked = !done && !d.template_is_published;
          const pending = d.pending_group;

          let subtitle: string;
          let actionLabel: string;
          if (done) {
            subtitle = 'Signert på vegne av organisasjonen';
            actionLabel = 'Vis status';
          } else if (pending) {
            const waiting = pending.signatories.filter((s: any) => !s.signed);
            const names = waiting.map((s: any) => s.full_name).slice(0, 3).join(', ')
                        + (waiting.length > 3 ? ` + ${waiting.length - 3} til` : '');
            subtitle = `Signeringsrunde pågår — venter på ${names} (${pending.signed_count}/${pending.total} signert)`;
            actionLabel = 'Se status / avbryt';
          } else if (blocked) {
            subtitle = 'Venter på publisering fra Kompetanseforbundet';
            actionLabel = label;
          } else {
            subtitle = 'Må signeres på vegne av organisasjonen';
            actionLabel = `Signer ${label.toLowerCase()}`;
          }

          const isFelles = d.dpa_type === 'fellesskapsforpliktelse_studieforbund';
          const params = new URLSearchParams({
            type: d.dpa_type,
            party_type: d.party_type,
            party_id: d.party_id,
          });
          if (d.lms_type) params.set('lms_type', d.lms_type);
          if (d.lms_integration_id) params.set('lms_integration_id', d.lms_integration_id);

          stepList.push({
            key: d.dpa_type,
            title: label,
            subtitle,
            icon: isFelles ? FileSignature : Shield,
            done,
            required: !done,
            blocked,
            blockedReason: blocked
              ? 'Kompetanseforbundet har ikke publisert denne malen ennå. Kontakt Kompetanseforbundet hvis dette drøyer.'
              : undefined,
            actionLabel,
            actionHref: `/onboarding/dpa?${params.toString()}`,
          });
        }

        // Steg 2: Kvalitetskontrakt — kun for brukere tilknyttet en medlemsorganisasjon.
        // Studieforbund-admin og systemadmin får `reason: 'no_member_organization'` eller
        // `'internal_user'` fra backend — kvalitetskontrakten gjelder kursvirksomhet
        // (dvs. medlemsorganisasjonene) og ikke administrative nivåer.
        if (qRes?.success && qRes.reason !== 'no_member_organization' && qRes.reason !== 'internal_user') {
          const qDone = !qRes.required;
          stepList.push({
            key: 'quality',
            title: 'Kvalitetssikringskontrakt',
            subtitle: qDone
              ? 'Kontrakten er signert og bekreftet'
              : 'Bekreft 16 kvalitetskriterier for kursvirksomhet',
            icon: ClipboardCheck,
            done: qDone,
            required: !qDone,
            actionLabel: qDone ? 'Vis kontrakt' : 'Åpne kvalitetskontrakt',
            actionHref: '/onboarding/quality-assurance'
          });
        }

        setSteps(stepList);
        setAllDone(stepList.length > 0 && stepList.every(s => s.done));
      } catch (e: any) {
        setError(e.message || 'Kunne ikke hente onboarding-status');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-kf-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-red-900">Kunne ikke hente onboarding-status</h3>
            <p className="text-sm text-red-800 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Ingen onboarding-steg gjelder brukeren (f.eks. intern @kompetanseforbundet.no
  // som er unntatt kvalitetskontrakt, og har ingen DPA-krav)
  if (steps.length === 0) {
    return (
      <div className="max-w-2xl mx-auto p-8">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" />
          <h2 className="text-xl font-bold text-gray-900 mt-3">Ingen onboarding kreves</h2>
          <button onClick={() => navigate('/', { replace: true })}
                  className="mt-4 px-4 py-2 bg-kf-primary text-white rounded hover:bg-kf-primary-dark">
            Til dashboard
          </button>
        </div>
      </div>
    );
  }

  const doneCount = steps.filter(s => s.done).length;
  // "Neste"-fremheving går til første ikke-ferdig, ikke-blokkert steg.
  // Hvis alle gjenstående er blokkert, fremheves ingen — brukeren venter.
  const nextStep = steps.find(s => !s.done && !s.blocked);

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 lg:p-8">
      {/* Hero */}
      <div className="bg-kf-primary text-white rounded-t-lg px-6 py-6 border-t-4 border-kf-accent">
        <div className="flex items-start gap-4">
          <FileSignature className="w-10 h-10 text-kf-accent flex-shrink-0 mt-1" />
          <div>
            <h1 className="text-2xl font-bold">Velkommen{userName ? `, ${userName.split(' ')[0]}` : ''}</h1>
            <p className="text-sm text-white/90 mt-1">
              Før du kan bruke systemet, må organisasjonen signere avtalene som Kompetanseforbundet
              krever for kvalitetssikret drift. Dette gjøres én gang.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-t-0 border-gray-200 rounded-b-lg shadow-sm">
        {/* Fremdrift */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-700 font-medium">
              {allDone ? 'Alle steg fullført' : `${doneCount} av ${steps.length} steg fullført`}
            </span>
            <div className="flex-1 mx-4 h-2 bg-gray-200 rounded overflow-hidden">
              <div
                className="h-full bg-kf-primary transition-all"
                style={{ width: `${(doneCount / steps.length) * 100}%` }}
              />
            </div>
            <span className="text-xs text-gray-500">{Math.round((doneCount / steps.length) * 100)}%</span>
          </div>
        </div>

        {/* Steg-liste */}
        <div className="divide-y divide-gray-200">
          {steps.map((step, idx) => {
            const Icon = step.icon;
            const isNext = step === nextStep;
            const isActionable = isNext && !step.blocked;
            return (
              <div key={step.key} className={`px-6 py-5 ${isNext && !step.blocked ? 'bg-kf-blue-50/40' : step.blocked ? 'bg-yellow-50/40' : ''}`}>
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                    step.done ? 'bg-green-100' :
                    step.blocked ? 'bg-yellow-100' :
                    isNext ? 'bg-kf-primary text-white' : 'bg-gray-100'
                  }`}>
                    {step.done ? (
                      <CheckCircle2 className="h-6 w-6 text-green-600" />
                    ) : (
                      <Icon className={`h-5 w-5 ${step.blocked ? 'text-yellow-700' : isNext ? 'text-white' : 'text-gray-500'}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Steg {idx + 1}
                      </span>
                      {step.done && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Fullført</span>
                      )}
                      {isNext && !step.blocked && (
                        <span className="text-xs bg-kf-accent text-kf-primary px-2 py-0.5 rounded font-medium">Neste</span>
                      )}
                      {step.blocked && (
                        <span className="text-xs bg-yellow-200 text-yellow-900 px-2 py-0.5 rounded font-medium">Venter</span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 mt-1">{step.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{step.subtitle}</p>
                    {step.blockedReason && (
                      <p className="text-xs text-yellow-900 bg-yellow-50 border border-yellow-200 rounded px-2 py-2 mt-2">
                        <strong>Ikke klar for signering ennå:</strong> {step.blockedReason}
                      </p>
                    )}
                    {step.detail && !step.blockedReason && (
                      <p className="text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded px-2 py-1 mt-2">
                        {step.detail}
                      </p>
                    )}
                    {isActionable && (
                      <button
                        onClick={() => navigate(step.actionHref)}
                        className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-medium bg-kf-primary text-white hover:bg-kf-primary-dark"
                      >
                        {step.actionLabel} <ArrowRight className="h-4 w-4" />
                      </button>
                    )}
                    {step.done && (
                      <button
                        onClick={() => navigate(step.actionHref)}
                        className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
                      >
                        {step.actionLabel} <ArrowRight className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Ferdig-blokk */}
        {allDone && (
          <div className="px-6 py-6 bg-green-50 border-t border-green-200 text-center">
            <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
            <h2 className="text-lg font-bold text-gray-900 mt-2">Alle avtaler er signert</h2>
            <p className="text-sm text-gray-600 mt-1">
              Du har nå full tilgang til systemet.
            </p>
            <button
              onClick={() => navigate('/', { replace: true })}
              className="mt-4 px-6 py-2 bg-kf-primary text-white rounded hover:bg-kf-primary-dark"
            >
              Til dashboard
            </button>
          </div>
        )}

        {/* Alle gjenstående er blokkert (venter på Kompetanseforbundet) */}
        {!allDone && !nextStep && (
          <div className="px-6 py-6 bg-yellow-50 border-t border-yellow-200 text-center">
            <h2 className="text-lg font-bold text-yellow-900 mt-2">Venter på Kompetanseforbundet</h2>
            <p className="text-sm text-yellow-800 mt-1 max-w-xl mx-auto">
              Gjenstående steg kan ikke fullføres før Kompetanseforbundet har publisert nødvendige maler.
              Ta gjerne kontakt hvis dette drøyer.
            </p>
          </div>
        )}

      </div>
    </div>
  );
}
